import { useRef, useState, CSSProperties, useEffect } from "react";
import {
  Play,
  Pause,
  Volume2,
  VolumeX,
  ChevronDown,
  User,
  Users,
} from "lucide-react";
import { usePodcastPlayer } from "../../../context/player/Player";
import { isDesktop } from "react-device-detect";

const BAR_COUNT = 14;

const generateHeights = (count: number): number[] => {
  const arr: number[] = [];
  for (let i = 0; i < count; i++) {
    const base =
      28 + Math.round(72 * Math.abs(Math.sin((i / count) * Math.PI * 2.3 + i)));
    const jitter = ((i * 37) % 11) - 5;
    const h = Math.max(8, Math.min(100, base + jitter));
    arr.push(h);
  }
  return arr;
};

const BASE_HEIGHTS = generateHeights(BAR_COUNT);

const stylesColor = {
  filled: "#55B5DB",
  unfilled: "#9B9B9B",
  dragging: "#55B5DB",
};

const formatTime = (s: number) => {
  if (!isFinite(s) || isNaN(s) || s <= 0) return "0:00";
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60)
    .toString()
    .padStart(2, "0");
  return `${m}:${sec}`;
};

// Hook para el efecto de pulso - Mismo que en PodcastButton
const useAudioPulse = (isPlaying: boolean) => {
  const [pulseSize, setPulseSize] = useState(0);

  useEffect(() => {
    if (!isPlaying) {
      setPulseSize(0);
      return;
    }

    let animationFrameId: number;
    let speechPhase = 0;
    let lastUpdate = 0;
    let currentPulse = 0;

    const animatePulse = (timestamp: number) => {
      if (!lastUpdate) lastUpdate = timestamp;
      const delta = timestamp - lastUpdate;

      if (delta > 60) {
        const time = timestamp * 0.003;
        
        const conversationCycle = Math.sin(time * 0.5) * 0.5 + 0.5;
        const phraseRate = Math.sin(time * 2) * 0.4 + 0.4;
        const wordRate = Math.sin(time * 6) * 0.3;
        const syllableRate = Math.sin(time * 15) * 0.2;
        const randomVar = Math.random() * 0.1;
        
        const combined = (
          conversationCycle * 0.3 +
          phraseRate * 0.4 + 
          wordRate * 0.2 +
          syllableRate * 0.1 +
          randomVar
        );
        
        const naturalPause = Math.random() > 0.98 ? 0.2 : 1;
        const targetPulse = Math.min(1, Math.max(0.15, combined * naturalPause));
        
        currentPulse = currentPulse + (targetPulse - currentPulse) * 0.15;
        
        // ⚡⚡⚡ AMPLITUD AUMENTADA SIGNIFICATIVAMENTE ⚡⚡⚡
        const maxPulseSize = 12;
        setPulseSize(currentPulse * maxPulseSize);
        
        lastUpdate = timestamp;
      }
      
      animationFrameId = requestAnimationFrame(animatePulse);
    };

    animationFrameId = requestAnimationFrame(animatePulse);

    return () => {
      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId);
      }
      setPulseSize(0);
    };
  }, [isPlaying]);

  return pulseSize;
};

export const GlobalPodcastPlayer = () => {
  const {
    playerState,
    playPodcast,
    pausePodcast,
    setCurrentTime,
    setVolume,
    switchVoice,
    setSelectedVoice,
  } = usePodcastPlayer();

  const [isHovered, setIsHovered] = useState(false);
  const [isVolumeHovered, setIsVolumeHovered] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [dragProgress, setDragProgress] = useState(0);
  const [animatedProgress, setAnimatedProgress] = useState(0);
  const [isFromClick, setIsFromClick] = useState(false);
  const [isVolumeDragging, setIsVolumeDragging] = useState(false);
  const [dragVolume, setDragVolume] = useState(0);
  const [isAvatarDropdownOpen, setIsAvatarDropdownOpen] = useState(false);
  const [isManuallyExpanded, setIsManuallyExpanded] = useState(false);

  const barsRef = useRef<HTMLDivElement>(null);
  const volumeRef = useRef<HTMLDivElement>(null);
  const avatarDropdownRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const isDraggingRef = useRef<boolean>(false);
  const isVolumeDraggingRef = useRef<boolean>(false);
  const animationFrameRef = useRef<number>(0);
  const lastClickTimeRef = useRef<number>(0);
  const pagerElementRef = useRef<HTMLElement | null>(null);

  // Usar el estado centralizado del contexto
  const selectedAvatar = playerState.selectedVoice;

  // Determinar si mostrar el selector de avatar
  const shouldShowAvatarSelector = !!(
    playerState.currentPodcast && playerState.currentPodcast2
  );

  // CORREGIDO: Mostrar siempre que haya un podcast cargado y no esté visible en pantalla
  const shouldShowGlobal =
    (playerState.currentPodcast || playerState.currentPodcast2) &&
    !playerState.isCurrentPodcastVisible;

  // Usar el hook de pulso
  const pulseSize = useAudioPulse(playerState.isPlaying);

  // Función para toggle del estado expandido manualmente (solo móvil)
  const toggleManualExpand = () => {
    if (!isDesktop) {
      setIsManuallyExpanded(!isManuallyExpanded);
    }
  };

  // Determinar si mostrar controles
  const shouldShowControls = isDesktop ? isHovered : isManuallyExpanded;

  // Determinar si mostrar el botón de expandir (solo móvil)
  const shouldShowExpandButton = !isDesktop;

  // Estilo para el pulso del borde - ACTUALIZADO para coincidir con maxPulseSize = 12
  const getPulseBorderStyle = (): React.CSSProperties => {
    if (playerState.isPlaying && pulseSize > 0) {
      // ⚡ ACTUALIZADO: Cambia /7 por /12 para coincidir con el maxPulseSize
      const opacity = 0.3 + (pulseSize / 12) * 0.6; // ← /12 en lugar de /7
      return {
        border: "2px solid #55B5DB",
        boxShadow: `0 0 0 ${pulseSize}px rgba(85, 181, 219, ${opacity})`,
        transition: 'box-shadow 0.08s ease-out, border 0.08s ease-out', // ← Más rápida
      };
    }
    
    return {
      border: playerState.isPlaying ? "2px solid #55B5DB" : "2px solid #E5E7EB",
      boxShadow: 'none',
      transition: 'all 0.2s ease-out',
    };
  };

  // Efecto para manejar la clase "pager"
  useEffect(() => {
    if (!isDesktop) {
      if (!pagerElementRef.current) {
        pagerElementRef.current = document.querySelector(
          ".pager"
        ) as HTMLElement;
      }

      const pagerElement = pagerElementRef.current;

      if (pagerElement) {
        if (shouldShowControls) {
          pagerElement.classList.remove("pager");
        } else {
          pagerElement.classList.add("pager");
        }
      }
    }
  }, [shouldShowControls, isDesktop]);

  // Efecto de cleanup para restaurar la clase cuando el componente se desmonta
  useEffect(() => {
    return () => {
      if (!isDesktop && pagerElementRef.current) {
        pagerElementRef.current.classList.add("pager");
      }
    };
  }, []);

  // En móvil, cerrar expansión manual después de un tiempo de inactividad
  useEffect(() => {
    if (!isDesktop && isManuallyExpanded) {
      const timer = setTimeout(() => {
        setIsManuallyExpanded(false);
      }, 10000);

      return () => clearTimeout(timer);
    }
  }, [isManuallyExpanded, isDesktop]);

  // Cerrar dropdown al hacer clic fuera
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        avatarDropdownRef.current &&
        !avatarDropdownRef.current.contains(event.target as Node)
      ) {
        setIsAvatarDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Animación suave del progreso - funcionar incluso cuando está pausado
  useEffect(() => {
    if (isDragging) {
      setAnimatedProgress(dragProgress);
      return;
    }

    if (isFromClick) {
      setAnimatedProgress(playerState.currentTime);
      const timeout = setTimeout(() => {
        setIsFromClick(false);
      }, 100);
      return () => clearTimeout(timeout);
    }

    let startTime: number;
    let startProgress = animatedProgress;
    let targetProgress = playerState.currentTime;

    const animate = (timestamp: number) => {
      if (!startTime) startTime = timestamp;
      const elapsed = timestamp - startTime;

      const durationMs = 800;
      const progress = Math.min(elapsed / durationMs, 1);

      const easeOut = (t: number) => 1 - Math.pow(1 - t, 3);

      const newProgress =
        startProgress + (targetProgress - startProgress) * easeOut(progress);
      setAnimatedProgress(newProgress);

      if (progress < 1) {
        animationFrameRef.current = requestAnimationFrame(animate);
      }
    };

    animationFrameRef.current = requestAnimationFrame(animate);

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [playerState.currentTime, isDragging, dragProgress, isFromClick]);

  const togglePlay = () => {
    const currentUrl =
      selectedAvatar === "female" && playerState.currentPodcast2
        ? playerState.currentPodcast2
        : playerState.currentPodcast;

    if (!currentUrl) return;

    if (playerState.isPlaying) {
      pausePodcast();
    } else {
      playPodcast(
        currentUrl,
        playerState.title,
        playerState.subsectionId || ""
      );
    }
  };

  useEffect(() => {
    if (
      isAvatarDropdownOpen &&
      avatarDropdownRef.current &&
      containerRef.current
    ) {
      const dropdown = avatarDropdownRef.current;
      const container = containerRef.current;
      const rect = container.getBoundingClientRect();

      // Verificar si hay espacio a la derecha
      const spaceRight = window.innerWidth - rect.right;
      const dropdownWidth = 200;

      if (spaceRight < dropdownWidth) {
        dropdown.style.right = "0";
        dropdown.style.left = "auto";
      } else {
        dropdown.style.left = "0";
        dropdown.style.right = "auto";
      }

      const spaceBottom = window.innerHeight - rect.bottom;
      const dropdownHeight = 160;

      if (spaceBottom < dropdownHeight) {
        dropdown.style.bottom = "calc(100% + 8px)";
        dropdown.style.top = "auto";
      } else {
        dropdown.style.top = "calc(100% + 8px)";
        dropdown.style.bottom = "auto";
      }
    }
  }, [isAvatarDropdownOpen]);

  // Función para cambiar de voz
  const handleAvatarSelect = (avatar: "male" | "female") => {
    const wasPlaying = playerState.isPlaying;
    const currentTime = playerState.currentTime;

    let newUrl: string;
    if (avatar === "male") {
      newUrl = playerState.currentPodcast || "";
    } else {
      newUrl = playerState.currentPodcast2 || "";
    }

    if (!newUrl) return;

    setSelectedVoice(avatar);
    setIsAvatarDropdownOpen(false);

    if (wasPlaying && newUrl) {
      pausePodcast();
      setTimeout(() => {
        switchVoice(newUrl);
        setTimeout(() => {
          playPodcast(
            playerState.currentPodcast || "",
            playerState.title,
            playerState.subsectionId || "",
            playerState.currentPodcast2 || undefined
          );
          setCurrentTime(currentTime);
        }, 100);
      }, 50);
    } else if (newUrl) {
      switchVoice(newUrl);
    }
  };

  const handleDragStart = (e: React.MouseEvent) => {
    const el = barsRef.current;
    if (!el || !playerState.duration) return;

    isDraggingRef.current = true;
    setIsDragging(true);

    const rect = el.getBoundingClientRect();

    const calculateTimeFromMouse = (clientX: number) => {
      const x = Math.max(0, Math.min(rect.width, clientX - rect.left));
      const ratio = Math.max(0, Math.min(1, x / rect.width));
      return ratio * playerState.duration;
    };

    const initialTime = calculateTimeFromMouse(e.clientX);
    setDragProgress(initialTime);

    const handleMouseMove = (moveEvent: MouseEvent) => {
      const newTime = calculateTimeFromMouse(moveEvent.clientX);
      setDragProgress(newTime);
    };

    const handleMouseUp = (upEvent: MouseEvent) => {
      const finalTime = calculateTimeFromMouse(upEvent.clientX);

      if (playerState.duration > 0) {
        setIsFromClick(true);
        setCurrentTime(finalTime);
        setAnimatedProgress(finalTime);
      }

      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
      isDraggingRef.current = false;
      setIsDragging(false);
    };

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
  };

  const seekByClick = (e: React.MouseEvent) => {
    const now = Date.now();
    if (now - lastClickTimeRef.current < 200) return;
    lastClickTimeRef.current = now;

    const el = barsRef.current;
    if (!el || !playerState.duration || playerState.duration <= 0) return;

    const rect = el.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const ratio = Math.max(0, Math.min(1, x / rect.width));
    const newTime = ratio * playerState.duration;

    setIsFromClick(true);
    setCurrentTime(newTime);
  };

  const handleVolumeDragStart = (e: React.MouseEvent) => {
    const el = volumeRef.current;
    if (!el) return;

    isVolumeDraggingRef.current = true;
    setIsVolumeDragging(true);

    const rect = el.getBoundingClientRect();

    const calculateVolumeFromMouse = (clientX: number) => {
      const x = Math.max(0, Math.min(rect.width, clientX - rect.left));
      const ratio = Math.max(0, Math.min(1, x / rect.width));
      return ratio;
    };

    const initialVolume = calculateVolumeFromMouse(e.clientX);
    setDragVolume(initialVolume);
    setVolume(initialVolume);

    const handleMouseMove = (moveEvent: MouseEvent) => {
      const newVolume = calculateVolumeFromMouse(moveEvent.clientX);
      setDragVolume(newVolume);
      setVolume(newVolume);
    };

    const handleMouseUp = (upEvent: MouseEvent) => {
      const finalVolume = calculateVolumeFromMouse(upEvent.clientX);
      setVolume(finalVolume);

      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
      isVolumeDraggingRef.current = false;
      setIsVolumeDragging(false);
    };

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
  };

  const handleVolumeClick = (e: React.MouseEvent) => {
    const now = Date.now();
    if (now - lastClickTimeRef.current < 200) return;
    lastClickTimeRef.current = now;

    const el = volumeRef.current;
    if (!el) return;

    const rect = el.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const ratio = Math.max(0, Math.min(1, x / rect.width));

    setVolume(ratio);
  };

  const toggleMute = () => {
    setVolume(playerState.volume > 0 ? 0 : 1);
  };

  const toggleAvatarDropdown = () => {
    if (shouldShowAvatarSelector) {
      setIsAvatarDropdownOpen(!isAvatarDropdownOpen);
    }
  };

  const currentProgress = isDragging ? dragProgress : animatedProgress;
  const percent =
    playerState.duration > 0
      ? Math.min(1, Math.max(0, currentProgress / playerState.duration))
      : 0;
  const filledCount = Math.min(
    BAR_COUNT,
    Math.max(0, Math.floor(percent * BAR_COUNT))
  );

  const currentVolume = isVolumeDragging ? dragVolume : playerState.volume;

  useEffect(() => {
    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, []);

  if (!shouldShowGlobal) {
    return null;
  }

  return (
    <div
      ref={containerRef}
      style={styles.container}
      onMouseEnter={() => isDesktop && setIsHovered(true)}
      onMouseLeave={() => {
        if (isDesktop) {
          setIsHovered(false);
          setIsAvatarDropdownOpen(false);
        }
      }}
    >
      <div
        style={{
          ...styles.mainContainer,
          background: "#F2F6F7",
          ...getPulseBorderStyle(), // Aplicar el estilo de pulso
        }}
      >
        {/* Contenedor del botón de play con posición relativa */}
        <div style={{ position: "relative", display: "inline-block" }}>
          <button
            style={{
              ...styles.playButton,
              background: "#DCE6E7",
            }}
            onClick={togglePlay}
            aria-label={playerState.isPlaying ? "Pausar" : "Reproducir"}
          >
            {playerState.isPlaying ? (
              <Pause
                size={16}
                color="black"
                style={{ fill: "black", stroke: "black" }}
              />
            ) : (
              <Play
                size={16}
                color="black"
                style={{ fill: "black", stroke: "black" }}
              />
            )}
          </button>

          {/* Botón pequeño para expandir/contraer - SOLO EN MÓVIL */}
          {shouldShowExpandButton && (
            <button
              style={{
                ...styles.expandButton,
                background: shouldShowControls
                  ? "#FCF123"
                  : "rgba(255, 255, 255, 0.95)",
                color: shouldShowControls ? "#000" : "#666",
              }}
              onClick={toggleManualExpand}
              aria-label={
                shouldShowControls ? "Contraer controles" : "Expandir controles"
              }
            >
              <i className={`icon icon-vpro-more-options-vertical`}></i>
            </button>
          )}
        </div>

        {shouldShowControls && (
          <div style={styles.controlsContainer}>
            <div style={styles.progressSection}>
              <div style={styles.timeText1}>{formatTime(currentProgress)}</div>

              <div
                ref={barsRef}
                style={styles.barsContainer}
                onMouseDown={handleDragStart}
                onClick={seekByClick}
                role="slider"
                aria-label="Barra de progreso"
              >
                {BASE_HEIGHTS.map((h, i) => {
                  const isFilled = i < filledCount;
                  const isPartiallyFilled = i === filledCount;
                  const currentColor = isDragging
                    ? stylesColor.dragging
                    : stylesColor.filled;

                  if (isFilled) {
                    return (
                      <div
                        key={i}
                        style={{
                          ...styles.singleBar,
                          height: `${h}%`,
                          background: currentColor,
                          borderRadius: 4,
                          transition: isFromClick
                            ? "background-color 50ms ease, height 200ms ease"
                            : "background-color 200ms ease, height 200ms ease",
                        }}
                      />
                    );
                  }

                  if (isPartiallyFilled && !isFromClick) {
                    const fillPercentage =
                      (percent * BAR_COUNT - filledCount) * 100;
                    return (
                      <div
                        key={i}
                        style={{
                          ...styles.singleBar,
                          height: `${h}%`,
                          backgroundImage: `linear-gradient(90deg, ${currentColor} 0%, ${currentColor} ${fillPercentage}%, ${stylesColor.unfilled} ${fillPercentage}%, ${stylesColor.unfilled} 100%)`,
                          borderRadius: 4,
                          transition:
                            "background-image 100ms ease, height 200ms ease",
                        }}
                      />
                    );
                  }

                  return (
                    <div
                      key={i}
                      style={{
                        ...styles.singleBar,
                        height: `${h}%`,
                        background: stylesColor.unfilled,
                        borderRadius: 4,
                        transition:
                          "background-color 200ms ease, height 200ms ease",
                      }}
                    />
                  );
                })}
              </div>

              <div style={styles.timeText}>
                {formatTime(playerState.duration)}
              </div>
            </div>

            <div style={styles.volumeSection}>
              <button
                style={styles.volumeButton}
                onClick={toggleMute}
                aria-label={
                  playerState.volume > 0 ? "Silenciar" : "Activar sonido"
                }
              >
                {playerState.volume > 0 ? (
                  <Volume2 size={14} />
                ) : (
                  <VolumeX size={14} />
                )}
              </button>

              <div
                ref={volumeRef}
                style={styles.volumeSliderContainer}
                onMouseDown={handleVolumeDragStart}
                onClick={handleVolumeClick}
                onMouseEnter={() => setIsVolumeHovered(true)}
                onMouseLeave={() => setIsVolumeHovered(false)}
                role="slider"
                aria-label="Control de volumen"
              >
                <div
                  style={{
                    ...styles.volumeSliderTrack,
                    height: isVolumeHovered ? "5px" : "3px",
                  }}
                >
                  <div
                    style={{
                      ...styles.volumeSliderFill,
                      width: `${currentVolume * 100}%`,
                      transition: isVolumeDragging ? "none" : "width 0.1s ease",
                    }}
                  />
                  <div
                    style={{
                      ...styles.volumeSliderThumb,
                      left: `${currentVolume * 100}%`,
                      opacity: isVolumeHovered || isVolumeDragging ? 1 : 0,
                    }}
                  />
                </div>
              </div>
            </div>

            {shouldShowAvatarSelector && (
              <div style={styles.avatarSelector}>
                <button
                  style={{
                    ...styles.avatarTrigger,
                    background: "transparent",
                    border: "none",
                    cursor: shouldShowAvatarSelector ? "pointer" : "default",
                    opacity: shouldShowAvatarSelector ? 1 : 0.5,
                  }}
                  onClick={toggleAvatarDropdown}
                  aria-label="Seleccionar avatar de voz"
                  aria-expanded={isAvatarDropdownOpen}
                  disabled={!shouldShowAvatarSelector}
                >
                  {selectedAvatar === "male" ? (
                    <User size={20} color="#55B5DB" />
                  ) : (
                    <Users size={20} color="#EC4899" />
                  )}
                  {shouldShowAvatarSelector && (
                    <ChevronDown
                      size={12}
                      color="#6B7280"
                      style={{
                        transform: isAvatarDropdownOpen
                          ? "rotate(180deg)"
                          : "rotate(0)",
                        transition: "transform 0.2s ease",
                      }}
                    />
                  )}
                </button>

                {isAvatarDropdownOpen && (
                  <div ref={avatarDropdownRef} style={styles.avatarDropdown}>
                    <div style={styles.avatarDropdownContent}>
                      <div style={styles.avatarDropdownHeader}>
                        <span style={styles.avatarDropdownTitle}>
                          Seleccionar voz
                        </span>
                      </div>

                      <button
                        style={{
                          ...styles.avatarOption,
                          background:
                            selectedAvatar === "male"
                              ? "#f0f9ff"
                              : "transparent",
                          border:
                            selectedAvatar === "male"
                              ? "1px solid #55B5DB"
                              : "1px solid transparent",
                        }}
                        onClick={() => handleAvatarSelect("male")}
                      >
                        <div
                          style={{
                            ...styles.avatarOptionIcon,
                            background: "#dbeafe",
                          }}
                        >
                          <User size={14} color="#55B5DB" />
                        </div>
                        <div style={styles.avatarOptionText}>
                          <span style={styles.avatarLabel}>Voz Masculina</span>
                        </div>
                        {selectedAvatar === "male" && (
                          <div style={styles.selectedIndicator} />
                        )}
                      </button>

                      <button
                        style={{
                          ...styles.avatarOption,
                          background:
                            selectedAvatar === "female"
                              ? "#fdf2f8"
                              : "transparent",
                          border:
                            selectedAvatar === "female"
                              ? "1px solid #EC4899"
                              : "1px solid transparent",
                        }}
                        onClick={() => handleAvatarSelect("female")}
                      >
                        <div
                          style={{
                            ...styles.avatarOptionIcon,
                            background: "#fce7f3",
                          }}
                        >
                          <Users size={14} color="#EC4899" />
                        </div>
                        <div style={styles.avatarOptionText}>
                          <span style={styles.avatarLabel}>Voz Femenina</span>
                        </div>
                        {selectedAvatar === "female" && (
                          <div style={styles.selectedIndicator} />
                        )}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      <style>{`
        @keyframes slideInFromLeft {
          from {
            opacity: 0;
            transform: translateX(-10px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }
        
        @keyframes dropdownFadeIn {
          from {
            opacity: 0;
            transform: translateY(-8px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </div>
  );
};

const styles: { [key: string]: CSSProperties } = {
  container: {
    position: "fixed",
    left: "20px",
    bottom: "20px",
    zIndex: 10000,
    display: "inline-flex",
    alignItems: "center",
  },
  mainContainer: {
    position: "relative",
    height: "50px",
    display: "flex",
    alignItems: "center",
    padding: "8px",
    transition: "all 0.3s ease",
    overflow: "visible",
    minWidth: "50px",
    borderRadius: "25px",
    gap: "5px",
    background: "#F2F6F7",
  },
  playButton: {
    width: "34px",
    height: "34px",
    borderRadius: "50%",
    color: "#fff",
    border: "none",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    transition: "all 0.3s ease",
    flexShrink: 0,
    marginLeft: -1.3,
    background: "#DCE6E7",
    position: "relative",
  },
  expandButton: {
    position: "absolute",
    bottom: "-17px",
    right: "-17px",
    width: "25px",
    height: "25px",
    borderRadius: "50%",
    border: "none",
    background: "rgba(255, 255, 255, 0.95)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    boxShadow: "0 1px 3px rgba(0, 0, 0, 0.3)",
    zIndex: 10001,
    padding: 0,
    transition: "all 0.2s ease",
    fontSize: "12px",
  },
  controlsContainer: {
    display: "flex",
    alignItems: "center",
    gap: "6px",
    marginRight: "8px",
    animation: "slideInFromLeft 0.3s ease-out",
    marginLeft: "0",
  },
  progressSection: {
    display: "flex",
    alignItems: "center",
    gap: "0px",
  },
  timeText: {
    fontSize: "10px",
    color: "#374151",
    fontWeight: 600,
    minWidth: "32px",
    textAlign: "center",
    flexShrink: 0,
  },
  timeText1: {
    fontSize: "10px",
    color: "#55B5DB",
    fontWeight: 600,
    minWidth: "32px",
    textAlign: "center",
    flexShrink: 0,
  },
  barsContainer: {
    display: "grid",
    gridTemplateColumns: `repeat(${BAR_COUNT}, 1fr)`,
    alignItems: "center",
    gap: 4,
    height: 30,
    width: "100%",
    maxWidth: 600,
    padding: "0 2px",
    borderRadius: 8,
    cursor: "pointer",
    userSelect: "none",
    minWidth: "90px",
  },
  singleBar: {
    width: "100%",
    alignSelf: "center",
    borderRadius: 4,
    transition: "background-color 200ms ease, height 200ms ease",
  },
  volumeSection: {
    display: "flex",
    alignItems: "center",
    gap: "3px",
  },
  volumeButton: {
    width: "26px",
    height: "26px",
    borderRadius: "50%",
    border: "none",
    background: "transparent",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    color: "#374151",
    transition: "all 0.2s ease",
    flexShrink: 0,
  },
  volumeSliderContainer: {
    minWidth: "40px",
    cursor: "pointer",
    padding: "4px 0",
    userSelect: "none",
  },
  volumeSliderTrack: {
    width: "100%",
    background: "#e5e7eb",
    borderRadius: "2px",
    position: "relative",
    cursor: "grab",
    transition: "height 0.2s ease",
  },
  volumeSliderFill: {
    position: "absolute",
    left: 0,
    top: 0,
    height: "100%",
    background: "#55B5DB",
    borderRadius: "2px",
  },
  volumeSliderThumb: {
    position: "absolute",
    top: "50%",
    transform: "translate(-50%, -50%)",
    width: "12px",
    height: "12px",
    borderRadius: "50%",
    background: "#55B5DB",
    boxShadow: "0 1px 2px rgba(0, 0, 0, 0.2)",
    transition: "opacity 0.2s ease, left 0.1s ease",
    cursor: "grab",
  },
  avatarSelector: {
    position: "relative",
    paddingLeft: "3px",
  },
  avatarTrigger: {
    display: "flex",
    alignItems: "center",
    background: "transparent",
    border: "none",
    borderRadius: "0",
    padding: "0",
    cursor: "pointer",
    transition: "all 0.2s ease",
    minWidth: "auto",
  },
  avatarDropdown: {
    position: "absolute",
    top: "calc(100% + 8px)",
    left: "0",
    zIndex: 10002,
    minWidth: "200px",
    maxWidth: "calc(100vw - 40px)",
  },
  avatarDropdownContent: {
    background: "white",
    borderRadius: "12px",
    boxShadow: "0 10px 25px rgba(0, 0, 0, 0.15), 0 4px 12px rgba(0, 0, 0, 0.1)",
    border: "1px solid #e5e7eb",
    overflow: "hidden",
    animation: "dropdownFadeIn 0.2s ease-out",
  },
  avatarDropdownHeader: {
    padding: "12px 16px 8px",
    borderBottom: "1px solid #f3f4f6",
  },
  avatarDropdownTitle: {
    fontSize: "12px",
    fontWeight: 600,
    color: "#6B7280",
    textTransform: "uppercase",
    letterSpacing: "0.05em",
  },
  avatarOption: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    width: "100%",
    padding: "12px 16px",
    border: "1px solid transparent",
    background: "transparent",
    cursor: "pointer",
    transition: "all 0.2s ease",
    position: "relative",
  },
  avatarOptionIcon: {
    width: "32px",
    height: "32px",
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
  avatarOptionText: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    gap: "2px",
    flex: 1,
  },
  avatarLabel: {
    fontSize: "14px",
    fontWeight: 600,
    color: "#111827",
  },
  selectedIndicator: {
    width: "8px",
    height: "8px",
    borderRadius: "50%",
    background: "#55B5DB",
    flexShrink: 0,
  },
};